import { useState } from 'react';
import * as R from 'ramda';

import FontIconButton from '@atom/components/common/FontIconButton';
import { buildAssetFieldName } from '@atom/selectors/formSelectors';
import colors from '@atom/styles/colors';
import { AssetSchemaType, AttributeGroupItem } from '@atom/types/form';
import {
  AttributesType,
  HydratedAttributeGroupsItem,
  HydratedInventorySchemaItem,
} from '@atom/types/inventory';
import { SchemaType } from '@atom/types/schema';
import schemaUtilities from '@atom/utilities/schemaUtilities';

import AttributeGroupBlock from './AttributeGroupBlock';

import classes from '../formBuilder.module.css';

const styles = {
  icon: {
    padding: '0px',
    height: '35px',
    width: '35px',
  },
};

interface Props {
  fullSchema: HydratedInventorySchemaItem;
  assetSchema: AssetSchemaType;
  rootSchema: SchemaType;
  fieldId: string;
  updateAttributeGroup: (
    fieldId: string,
    schemaId: string,
    addGroup: boolean,
    attributeGroup: AttributeGroupItem,
  ) => void;
  updateAttribute: (
    fieldId: string,
    schemaId: string,
    addAttribute: boolean,
    attributeGroupName: string,
    attribute: AttributesType,
  ) => void;
  addAllAttributes: (
    fieldId: string,
    schemaId: string,
    attributeGroupName: string,
    attributes: AttributesType[],
  ) => void;
  removeAssetSchema: (fieldId: string, schemaId: string) => void;
}

const AssetField = ({
  assetSchema,
  fullSchema,
  rootSchema,
  updateAttributeGroup,
  updateAttribute,
  addAllAttributes,
  fieldId,
  removeAssetSchema,
}: Props) => {
  const [open, setOpen] = useState<boolean>(true);

  const getAssetAttributeGroup = (
    name: string,
  ): HydratedAttributeGroupsItem | Object => {
    if (R.isNil(assetSchema.attributeGroups)) {
      return {};
    }

    const matchedAssetGroup = assetSchema.attributeGroups.find(
      (group: HydratedAttributeGroupsItem): boolean => group.name === name,
    );

    return matchedAssetGroup ? matchedAssetGroup : {};
  };

  const collapseIcon = open ? 'keyboard_arrow_up' : 'keyboard_arrow_down';
  const assetFieldName = buildAssetFieldName(
    assetSchema.name,
    assetSchema.ancestors,
  );
  const leftIcon = schemaUtilities.getSchemaIconFromSchemaOrAsset(rootSchema);

  return (
    <div className={classes.schemaFieldContainer}>
      <div className={classes.schemaFieldHeader}>
        <div className={classes.schemaLeftHeader}>
          {leftIcon}
          <div className={classes.schemaHeaderTitle}>{assetFieldName}</div>
        </div>
        <div>
          <FontIconButton
            style={styles.icon}
            tooltip="Delete"
            onClick={(): void => removeAssetSchema(fieldId, fullSchema.id)}
            color={colors.neutral.gray}
            icon="delete"
            tooltipPosition="bottom-left"
          />
          <FontIconButton
            style={styles.icon}
            onClick={() => setOpen(!open)}
            color={colors.neutral.gray}
            icon={collapseIcon}
          />
        </div>
      </div>
      {open &&
        fullSchema.attributeGroups.map(
          (fullAttributeGroup: HydratedAttributeGroupsItem) => {
            const assetAttributeGroup = getAssetAttributeGroup(
              fullAttributeGroup.name,
            );

            return (
              <AttributeGroupBlock
                updateAttributeGroup={updateAttributeGroup}
                key={fullAttributeGroup.name}
                fieldId={fieldId}
                schemaId={fullSchema.id}
                fullAttributeGroup={fullAttributeGroup}
                // @ts-ignore
                assetAttributeGroup={assetAttributeGroup}
                updateAttribute={updateAttribute}
                addAllAttributes={addAllAttributes}
              />
            );
          },
        )}
    </div>
  );
};

export default AssetField;
